import type { PieSeriesOption } from "echarts/charts";
import { PieChart } from "echarts/charts";
import type {
  TitleComponentOption,
  TooltipComponentOption,
} from "echarts/components";
import { TitleComponent, TooltipComponent } from "echarts/components";
import type { ComposeOption } from "echarts/core";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import ReactEcharts from "echarts-for-react";
import { Ref, useImperativeHandle, useRef } from "react";

// Reduce bundle size: see https://echarts.apache.org/handbook/en/basics/import/#shrinking-bundle-size
echarts.use([PieChart, CanvasRenderer, TitleComponent, TooltipComponent]);

// Create an Option Type: see https://echarts.apache.org/handbook/en/basics/import/#creating-an-option-type-in-typescript
export type ECOption = ComposeOption<
  PieSeriesOption | TitleComponentOption | TooltipComponentOption
>;

export type PieChartRef = {
  highlight: (key: string | undefined) => void;
};

type PieChartData = {
  value: string;
  name: string;
  key: string | undefined;
  itemStyle: {
    color: string;
  };
};

type ReactPieChartProps = {
  option: ECOption;
  onHover?: (key: string | undefined) => void;
  onClick?: (key: string | undefined) => void;
  ref?: Ref<PieChartRef>;
};

function ReactPieChart({ option, onHover, onClick, ref }: ReactPieChartProps) {
  const echartsRef = useRef<ReactEcharts>(null);

  const hoverEvents:
    | Record<string, ({ data }: { data: PieChartData }) => void>
    | undefined = onHover
    ? {
        mouseover: ({ data }: { data: PieChartData }) => {
          onHover(data.key);
        },
        mouseout: () => {
          onHover(undefined);
        },
      }
    : undefined;

  const clickEvents:
    | Record<string, ({ data }: { data: PieChartData }) => void>
    | undefined = onClick
    ? {
        click: ({ data }: { data: PieChartData }) => {
          onClick(data.key);
        },
      }
    : undefined;

  const chart = (
    <ReactEcharts
      style={{ height: "100%", width: "100%" }}
      echarts={echarts}
      ref={echartsRef}
      option={option}
      onEvents={{
        ...hoverEvents,
        ...clickEvents,
      }}
    />
  );

  useImperativeHandle(ref, () => {
    return {
      highlight(name: string | undefined) {
        if (echartsRef.current) {
          if (name !== undefined) {
            echartsRef.current.getEchartsInstance().dispatchAction({
              type: "highlight",
              seriesIndex: 0,
              name: name,
            });
          } else {
            echartsRef.current.getEchartsInstance().dispatchAction({
              type: "downplay",
              seriesIndex: 0,
            });
          }
        }
      },
    };
  }, []);

  return chart;
}

ReactPieChart.displayName = "ReactPieChart";

export default ReactPieChart;
